import { createHash } from 'crypto';
import cookies from 'browser-cookies';

import {
	authorizerTokenCookie,
	authorizerTokenCookieExpirationDate,
	forgeAccessTokenExpiration,
	forgeRefreshTokenCookie,
	forgeRevalidationCookie,
} from '../types/authenticationContext.types';
import { Environments, getEnvironment } from '../utils/environment';

function hash(string: string) {
	return createHash('sha256').update(string).digest('hex');
}

export const sanitizeSharedWorkerMessageData = (
	data: Record<string, unknown>,
) => {
	const sanitizedData = { ...data };
	const keys = Object.keys(sanitizedData);
	if (keys.length) {
		keys.forEach(key => {
			if (key.toLowerCase().includes('token')) {
				sanitizedData[key] = hash(String(sanitizedData[key]));
			}
		});
	}
	return sanitizedData;
};

export interface ForgeInitPayload {
	accessToken?: string;
	refresh_token?: string;
	refreshExpirationDate?: string;
	finalExpirationDate: string;
	expirationDate?: string;
	revalidationDate?: string;
	modalOpenBeforeExpireInMinutes?: number;
	forceExpireInMinutes?: number;
}

export interface Auth0InitPayload {
	accessToken?: string;
	expirationDate?: string;
	finalExpirationDate: string;
	redirectUri: string;
}

export interface ExtendSessionPayload {
	expirationDate?: string;
}

// Query-string overrides for quick local testing without editing site-config.
// Example: ?forgeModalOpenBeforeExpireInMinutes=1&forgeForceExpireInMinutes=3
const forgeModalOverrideQueryKey = 'forgeModalOpenBeforeExpireInMinutes';
const forgeForceExpireOverrideQueryKey = 'forgeForceExpireInMinutes';

const parseOptionalNumber = (rawValue?: string | null): number | undefined => {
	if (!rawValue) return undefined;
	const parsed = Number(rawValue);
	return Number.isFinite(parsed) ? parsed : undefined;
};

const getForgeTimingOverrides = (): {
	modalOpenBeforeExpireInMinutes?: number;
	forceExpireInMinutes?: number;
} => {
	// Query params are the only override mechanism we keep to avoid stateful flakiness.
	if (getEnvironment() === Environments.PROD) {
		return {};
	}
	const urlParams = new URLSearchParams(window.location.search);
	const modalFromQuery = parseOptionalNumber(
		urlParams.get(forgeModalOverrideQueryKey),
	);
	const forceFromQuery = parseOptionalNumber(
		urlParams.get(forgeForceExpireOverrideQueryKey),
	);

	return {
		modalOpenBeforeExpireInMinutes: modalFromQuery,
		forceExpireInMinutes: forceFromQuery,
	};
};

export const getForgeInitPayloadFromCookies = (): ForgeInitPayload | null => {
	const refreshToken = cookies.get(forgeRefreshTokenCookie) ?? undefined;
	const accessToken = cookies.get(authorizerTokenCookie) ?? undefined;
	const expirationDate = cookies.get(forgeAccessTokenExpiration) ?? undefined;
	const revalidationDate = cookies.get(forgeRevalidationCookie) ?? undefined;
	const overrides = getForgeTimingOverrides();

	if (!refreshToken && !accessToken && !expirationDate && !revalidationDate) {
		return null;
	}

	return {
		accessToken,
		refresh_token: refreshToken,
		refreshExpirationDate: revalidationDate,
		finalExpirationDate: '',
		expirationDate,
		revalidationDate,
		...overrides,
	};
};

export const getAuth0InitPayloadFromCookies = (): Auth0InitPayload => ({
	accessToken: cookies.get(authorizerTokenCookie) ?? undefined,
	expirationDate:
		cookies.get(authorizerTokenCookieExpirationDate) ?? undefined,
	finalExpirationDate: '',
	redirectUri: window.location.origin,
});

export const getExtendSessionPayloadFromCookies = (): ExtendSessionPayload => {
	const expirationDate =
		cookies.get(authorizerTokenCookieExpirationDate) ?? undefined;

	return expirationDate ? { expirationDate } : {};
};
